/**
 * ConfirmDialog + useConfirm -- shared destructive-action gate.
 *
 * Consistency Sweep (Feb 2026). Replaces every ``window.confirm()``
 * call in the app with a Shadcn ``AlertDialog`` that matches the
 * design language established for the Expense delete flow.
 *
 * ## Two ways to use it
 *
 * ### 1. Promise-based hook (preferred -- shortest diff at call sites)
 *
 *     const { confirm, dialog } = useConfirm();
 *
 *     const handleDelete = async () => {
 *       const ok = await confirm({
 *         title: "Delete this message?",
 *         description: "Recipients will still see it if they've " +
 *                      "already read it.",
 *         confirmLabel: "Delete",
 *         variant: "destructive",
 *         testIdPrefix: "chat-msg-delete",
 *       });
 *       if (!ok) return;
 *       await api.delete(...);
 *     };
 *
 *     return (
 *       <>
 *         {dialog}
 *         ...rest of UI...
 *       </>
 *     );
 *
 * ### 2. Declarative wrapper (when you already have your own state)
 *
 *     <ConfirmDialog
 *       open={showConfirm}
 *       onOpenChange={setShowConfirm}
 *       title="..." description="..."
 *       onConfirm={doTheThing}
 *     />
 *
 * ## Guarantees
 *
 * - Every dialog exposes ``data-testid`` markers of the shape
 *   ``{prefix}-content``, ``{prefix}-cancel``, ``{prefix}-confirm``
 *   so the QA harness can drive it deterministically.
 * - ``variant: "destructive"`` (the default) paints the confirm
 *   button rose; ``variant: "default"`` uses the primary gradient.
 * - Cancel-first ordering, focus on Cancel, Escape closes. Same as
 *   ``alert-dialog`` primitives out of the box.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useIsMobile } from "@/lib/viewport";

const variantBtnClass = {
  destructive: "bg-rose-500 text-white hover:bg-rose-600",
  default: "bg-gradient-primary text-white hover:brightness-110",
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  variant = "destructive",
  onConfirm,
  onCancel,
  testIdPrefix = "confirm-dialog",
}) {
  const isMobile = useIsMobile();
  const handleConfirm = () => {
    if (typeof onConfirm === "function") onConfirm();
  };
  const handleCancel = () => {
    if (typeof onCancel === "function") onCancel();
  };
  const btnClass = variantBtnClass[variant] || variantBtnClass.destructive;

  // Dispatch 14a -- on <768px render as a bottom Sheet so destructive
  // actions land where the user's thumb is + get the native mobile feel.
  // Desktop still gets the classic centered AlertDialog.
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          data-testid={`${testIdPrefix}-content`}
          className="bg-card/95 backdrop-blur-xl rounded-t-2xl pb-6"
          style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
        >
          <SheetHeader className="text-left">
            <SheetTitle>{title}</SheetTitle>
            {description && <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
          <div className="flex flex-col gap-2 pt-4">
            {/* Primary action first on mobile (thumb-reach), Cancel below. */}
            <button
              data-testid={`${testIdPrefix}-confirm`}
              onClick={handleConfirm}
              className={`h-11 rounded-xl font-medium ${btnClass}`}
            >
              {confirmLabel}
            </button>
            <button
              data-testid={`${testIdPrefix}-cancel`}
              onClick={() => { handleCancel(); onOpenChange?.(false); }}
              className="h-11 rounded-xl bg-white/5 border border-[var(--surface-border)] text-foreground/80"
            >
              {cancelLabel}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={`${testIdPrefix}-content`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription>
              {description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid={`${testIdPrefix}-cancel`}
            onClick={handleCancel}
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={`${testIdPrefix}-confirm`}
            className={btnClass}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Promise-based hook. Call ``confirm({...})`` -- returns a promise
 * that resolves to ``true`` on Confirm, ``false`` on Cancel / dismiss.
 * Render ``{dialog}`` once in your component tree so the modal has a
 * mount point.
 */
export function useConfirm() {
  const [config, setConfig] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setConfig(opts || {});
    });
  }, []);

  const settle = useCallback((result) => {
    const r = resolveRef.current;
    resolveRef.current = null;
    setConfig(null);
    if (r) r(result);
  }, []);

  const handleOpenChange = useCallback((next) => {
    // Escape / overlay-click closes with Cancel semantics.
    if (!next) settle(false);
  }, [settle]);

  const dialog = useMemo(() => {
    if (!config) return null;
    return (
      <ConfirmDialog
        open
        onOpenChange={handleOpenChange}
        onConfirm={() => settle(true)}
        onCancel={() => settle(false)}
        title={config.title || "Are you sure?"}
        description={config.description}
        confirmLabel={config.confirmLabel || "Delete"}
        cancelLabel={config.cancelLabel || "Cancel"}
        variant={config.variant || "destructive"}
        testIdPrefix={config.testIdPrefix || "confirm-dialog"}
      />
    );
  }, [config, handleOpenChange, settle]);

  return { confirm, dialog };
}

// -------------------------------------------------------------------
// Global confirm() -- mount ``<ConfirmDialogRoot />`` once at the App
// root and any module can then call ``await confirm({...})`` without
// wiring the hook per-component. Same promise contract as useConfirm.
// -------------------------------------------------------------------
let _globalRequest = null;

export function confirm(opts) {
  if (!_globalRequest) {
    // ConfirmDialogRoot hasn't mounted yet -- fall back to native
    // window.confirm so nothing silently no-ops. Should never happen
    // in practice because ConfirmDialogRoot is rendered in App.jsx.
    return Promise.resolve(window.confirm(opts?.title || "Are you sure?"));
  }
  return _globalRequest(opts);
}

export function ConfirmDialogRoot() {
  const [config, setConfig] = useState(null);
  const resolveRef = useRef(null);

  useEffect(() => {
    _globalRequest = (opts) => new Promise((resolve) => {
      resolveRef.current = resolve;
      setConfig(opts || {});
    });
    return () => { _globalRequest = null; };
  }, []);

  const settle = useCallback((result) => {
    const r = resolveRef.current;
    resolveRef.current = null;
    setConfig(null);
    if (r) r(result);
  }, []);
  const handleOpenChange = useCallback((next) => {
    if (!next) settle(false);
  }, [settle]);

  if (!config) return null;
  return (
    <ConfirmDialog
      open
      onOpenChange={handleOpenChange}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
      title={config.title || "Are you sure?"}
      description={config.description}
      confirmLabel={config.confirmLabel || "Delete"}
      cancelLabel={config.cancelLabel || "Cancel"}
      variant={config.variant || "destructive"}
      testIdPrefix={config.testIdPrefix || "confirm-dialog"}
    />
  );
}

export default ConfirmDialog;
